<template>
  <TransitionRoot as="template" :show="!!show && !!managedRegion">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-10">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-xl">
              <form class="flex h-full flex-col shellPanel">
                <!-- HEADER -->
                <div class="shrink-0 shellHeader">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <DialogTitle class="text-lg font-semibold text-white tracking-tight">
                        Edytuj region dostawy
                      </DialogTitle>

                      <div class="mt-2 flex flex-wrap items-center gap-2">
                        <span class="headerPill">
                          ID:
                          <span class="font-semibold text-white ml-1">
                            {{ managedRegion?.id || '—' }}
                          </span>
                        </span>
                      </div>

                      <p class="mt-3 text-sm text-slate-300">
                        Zmień nazwę i opis regionu albo usuń go z listy regionów tras.
                      </p>
                    </div>

                    <button
                      type="button"
                      class="iconBtn shrink-0"
                      @click.prevent="emitClose"
                      aria-label="Zamknij panel"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <!-- BODY -->
                <div class="min-h-0 flex-1 overflow-y-auto space-y-4 shellBody">
                  <!-- DANE REGIONU -->
                  <section class="sectionCard">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle">Dane regionu</div>
                        <div class="sectionSub">
                          Edytuj podstawowe informacje używane przy przypisywaniu regionów do tras i zamówień.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody gridGap">
                      <div class="fieldWrap">
                        <label class="fieldLabel">Nazwa regionu*</label>
                        <input
                          v-model="form.name"
                          type="text"
                          maxlength="60"
                          :disabled="deleteRegionOnSave"
                          :class="fieldClass(errors.name)"
                          placeholder="np. wielkopolska-południe"
                        />
                        <p v-if="errors.name" class="fieldError">
                          Podaj nazwę regionu.
                        </p>
                      </div>

                      <div class="fieldWrap">
                        <label class="fieldLabel">Opis regionu*</label>
                        <textarea
                          v-model="form.description"
                          maxlength="100"
                          rows="4"
                          :disabled="deleteRegionOnSave"
                          :class="fieldClass(errors.description)"
                          placeholder="np. Okolice Kalisza, Ostrzeszowa..."
                        ></textarea>
                        <p v-if="errors.description" class="fieldError">
                          Podaj opis regionu.
                        </p>
                      </div>
                    </div>
                  </section>

                  <!-- USUWANIE -->
                  <section class="sectionCard sectionDanger">
                    <div class="sectionHead">
                      <div>
                        <div class="sectionTitle text-red-300">Usuń region</div>
                        <div class="sectionSub">
                          Trwale usuń region z dokumentu routeRegions/regions.
                        </div>
                      </div>
                    </div>

                    <div class="sectionBody">
                      <label class="inline-flex items-start gap-3 cursor-pointer select-none">
                        <input
                          v-model="deleteRegionOnSave"
                          type="checkbox"
                          class="mt-1 h-4 w-4 rounded border-slate-300 text-red-500 focus:ring-red-500"
                        />
                        <span class="text-sm text-slate-100">
                          Chcę usunąć ten region – wiem co robię.
                        </span>
                      </label>

                      <p class="fieldHint mt-3" v-if="deleteRegionOnSave">
                        Region zostanie usunięty z listy.
                        <span class="font-semibold text-red-300">Proces jest nieodwracalny.</span>
                      </p>
                    </div>
                  </section>
                </div>

                <!-- FOOTER -->
                <div class="shrink-0 shellFooter">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-slate-300">
                      <span v-if="loading">Zapisywanie...</span>
                      <span v-else>Edycja regionu dostawy.</span>
                    </div>

                    <div class="flex items-center gap-2">
                      <button
                        v-if="!loading"
                        @click.prevent="emitClose"
                        type="button"
                        class="btnSecondary"
                      >
                        Zamknij
                      </button>

                      <button
                        v-if="!deleteRegionOnSave && !loading"
                        @click.prevent="validateAndSave"
                        type="button"
                        class="btnPrimary"
                      >
                        Aktualizuj
                      </button>

                      <button
                        v-if="deleteRegionOnSave && !loading"
                        type="button"
                        @click.prevent="deleteRegion"
                        class="btnDanger"
                      >
                        Usuń region
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { db } from '@/firebase/gfbconf.js'

export default {
  name: 'ModalManageRouteRegion',
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  props: {
    show: { type: Boolean, default: false },
    managedRegion: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      deleteRegionOnSave: false,
      form: {
        name: '',
        description: ''
      },
      errors: {
        name: false,
        description: false
      }
    }
  },
  watch: {
    managedRegion: {
      immediate: true,
      handler(nv) {
        if (!nv) return
        this.loading = false
        this.deleteRegionOnSave = false
        this.resetErrors()
        this.form.name = nv.name || ''
        this.form.description = nv.description || ''
      }
    }
  },
  methods: {
    fieldClass(hasError) {
      return [
        'fieldInput',
        hasError ? 'fieldInputError' : ''
      ]
    },

    resetErrors() {
      this.errors.name = false
      this.errors.description = false
    },

    validate() {
      this.resetErrors()
      let errs = 0

      if (!this.form.name || !this.form.name.trim()) {
        this.errors.name = true
        errs++
      }

      if (!this.form.description || !this.form.description.trim()) {
        this.errors.description = true
        errs++
      }

      return errs === 0
    },

    async validateAndSave() {
      if (!this.managedRegion?.id) return
      if (!this.validate()) return

      this.loading = true
      const regionId = this.managedRegion.id

      try {
        const docRef = db.collection('routeRegions').doc('regions')
        const snap = await docRef.get()

        if (!snap.exists) {
          throw new Error('Dokument routeRegions/regions nie istnieje.')
        }

        const data = snap.data() || {}
        const regions = Array.isArray(data.regions) ? [...data.regions] : []
        const idx = regions.findIndex((r) => r.id === regionId)

        if (idx === -1) {
          throw new Error('Region nie został znaleziony w tablicy.')
        }

        regions[idx] = {
          ...regions[idx],
          name: this.form.name.trim(),
          description: this.form.description.trim()
        }

        await docRef.set({ regions }, { merge: true })

        this.$store.commit('setNotification', {
          show: true,
          head: 'Region zaktualizowany!',
          subheader: 'Dane regionu zostały zapisane.',
          success: true
        })

        this.$emit('reloadRegions')
        this.emitClose()
      } catch (e) {
        console.error('Błąd aktualizacji regionu:', e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd zapisu regionu',
          subheader: 'Nie udało się zaktualizować regionu.',
          success: false
        })
        this.loading = false
      }
    },

    async deleteRegion() {
      if (!this.managedRegion?.id) return

      this.loading = true
      const regionId = this.managedRegion.id

      try {
        const docRef = db.collection('routeRegions').doc('regions')
        const snap = await docRef.get()

        if (!snap.exists) {
          throw new Error('Dokument routeRegions/regions nie istnieje.')
        }

        const data = snap.data() || {}
        const regions = Array.isArray(data.regions) ? data.regions : []
        const filtered = regions.filter((r) => r.id !== regionId)

        await docRef.set({ regions: filtered }, { merge: true })

        this.$store.commit('setNotification', {
          show: true,
          head: 'Region usunięty!',
          subheader: 'Region został usunięty z listy.',
          success: true
        })

        this.$emit('reloadRegions')
        this.emitClose()
      } catch (e) {
        console.error('Błąd usuwania regionu:', e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd usuwania regionu',
          subheader: 'Nie udało się usunąć regionu.',
          success: false
        })
        this.loading = false
      }
    },

    emitClose() {
      this.$emit('closeRegionModal')
      this.loading = false
      this.deleteRegionOnSave = false
      this.resetErrors()
    }
  }
}
</script>

<style scoped>
.backdrop-soft {
  backdrop-filter: blur(3px);
}

.shellPanel {
  height: 100%;
  background: #1e293b;
  border-left: 1px solid rgba(71, 85, 105, 0.9);
  box-shadow: -10px 0 35px rgba(0, 0, 0, 0.28);
}

.shellHeader {
  padding: 20px 20px 18px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.7);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.96));
}

.shellBody {
  padding: 16px;
  background: rgba(30, 41, 59, 0.98);
}

.shellFooter {
  padding: 14px 16px;
  border-top: 1px solid rgba(71, 85, 105, 0.7);
  background: rgba(15, 23, 42, 0.92);
}

.sectionCard {
  overflow: visible;
  position: relative;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.sectionDanger {
  border-color: rgba(127, 29, 29, 0.7);
  background: rgba(69, 10, 10, 0.18);
}

.sectionHead {
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.65);
}

.sectionTitle {
  font-size: 15px;
  line-height: 1.3;
  font-weight: 700;
  color: #f8fafc;
}

.sectionSub {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
}

.sectionBody {
  padding: 16px 18px 18px;
}

.gridGap > * + * {
  margin-top: 14px;
}

.headerPill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 9999px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(51, 65, 85, 0.7);
  font-size: 11px;
  line-height: 1;
  color: #cbd5e1;
}

.fieldWrap {
  display: block;
}

.fieldLabel {
  display: block;
  margin-bottom: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldInput {
  display: block;
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.fieldInput:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.fieldInput:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fieldInputError {
  border-color: rgba(239, 68, 68, 0.9) !important;
}

.fieldHint {
  margin-top: 7px;
  font-size: 11px;
  line-height: 1.45;
  color: #94a3b8;
}

.fieldError {
  margin-top: 6px;
  font-size: 12px;
  color: #fca5a5;
}

.iconBtn {
  height: 40px;
  width: 40px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  transition: 180ms ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary,
.btnPrimary,
.btnDanger {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  transition: 180ms ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.5);
  background: rgba(37, 99, 235, 0.2);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

.btnDanger {
  border: 1px solid rgba(239, 68, 68, 0.55);
  background: rgba(220, 38, 38, 0.2);
  color: #fecaca;
}

.btnDanger:hover {
  background: rgba(220, 38, 38, 0.3);
}

@media (min-width: 640px) {
  .shellHeader {
    padding: 24px 24px 20px;
  }

  .shellBody {
    padding: 18px;
  }

  .shellFooter {
    padding: 16px 18px;
  }
}
</style>